<script setup lang="ts">
import { faArrowRight, faClose } from '@fortawesome/pro-regular-svg-icons';
import TokenStep from './login-steps/TokenStep.vue';
import TheChangePassword from './login-steps/TheChangePassword.vue';
import TheEnterMobileOrEmailStep from './login-steps/TheEnterMobileOrEmailStep.vue';
import TheLoginByPasswordStep from './login-steps/TheLoginByPasswordStep.vue';
import TheSignupInfoStep from './login-steps/TheSignupInfoStep.vue';
import TheSwitchAccount from './login-steps/TheSwitchAccount.vue';
import TheForgetPassPhoneNumber from './login-steps/TheForgetPassPhoneNumber.vue';
import { AuthStep, type OpenedBySources } from '@store/auth/types';
import Regex from '@static/regex';

const {
    message,
    initialStep = AuthStep.EnterMobileOrEmail,
    mobile,
    disableSwitchAccount,
    required,
    openedBy,
} = defineProps<{
    message?: string;
    rejectionText?: string;
    openedBy?: OpenedBySources;
    initialStep?: AuthStep;
    mobile?: string;
    disableSwitchAccount?: boolean;
    required?: boolean;
}>();
const emit = defineEmits<{
    close: [successful: boolean];
}>();
const authStore = useAuthStore();
const analyticsStore = useAnalyticsStore();
const { checkToken, validateUsername } = authStore;
const { $success } = useNotify();
const { isMobile } = useSize();
const gtm = useGtm();
const stepHistory = ref<AuthStep[]>([]);
const mobileOrEmail = ref('');
const token = ref('');
const currentStep = computed(
    () => stepHistory.value[stepHistory.value.length - 1]
);

const hasBack = computed(() => stepHistory.value.length > 1);
let successful = false;

watch(currentStep, (step, previousStep) => {
    if (step) {
        analyticsStore.trackAll('login_step_view', {
            step,
            previousStep,
            openedBy,
        });
    }
});

onUnmounted(() => {
    analyticsStore.trackAll('login_flow_exit', {
        step: currentStep.value,
        successful,
        openedBy,
    });
});

const validateToken = async (flow: 'login' | 'password_reset') => {
    const result = await checkToken(
        validateUsername(mobileOrEmail.value, 'mobile'),
        token.value
    );
    analyticsStore.trackAll('login_otp_verify_result', {
        flow,
        status: result === null ? 'failed' : 'success',
        userExists: result === null ? undefined : result,
    });
    return result;
};

const loginByToken = async () => {
    try {
        const username = validateUsername(mobileOrEmail.value, 'mobile');
        const result = await validateToken('login');
        if (result === null) {
            return;
        }
        gtm?.trackEvent({
            event: 'login with code',
        });
        if (result) {
            const step = await authStore.loginByToken({
                token: token.value,
                username,
            });
            analyticsStore.trackAll('login_result', {
                method: 'otp',
                status: 'success',
            });
            analyticsStore.trackAll('logIn');
            onSubmit('با موفقیت وارد شدید!', step);
        } else {
            setStep(AuthStep.SignupInfo);
        }
    } catch (e) {
        analyticsStore.trackAll('login_result', {
            method: 'otp',
            status: 'failed',
        });
        reportException(e);
    }
};

async function checkChangePasswordToken() {
    const result = await validateToken('password_reset');
    if (result) {
        setStep(AuthStep.ChangePassword);
    } else if (result === false) {
        setStep(AuthStep.SignupInfo);
    }
}

function backUntil(step: AuthStep) {
    if (!stepHistory.value.length) {
        return;
    }
    do {
        stepHistory.value.pop();
    } while (currentStep.value !== step && stepHistory.value.length);
}

function setStep(step: AuthStep) {
    // Adding prev state to the history stack
    stepsGuard({ to: step, from: currentStep.value });
    stepHistory.value.push(step);
}

watch(mobileOrEmail, (newValue, oldValue) => {
    const input = convertPersianNumbers(newValue);
    let partialValid = true;
    if (currentStep.value === AuthStep.LoginByPassword) {
        validateUsername(newValue);
    } else {
        validateUsername(newValue, 'mobile');
        partialValid = Regex.mobile.partial.test(input);
    }
    if (!partialValid && input.length) {
        nextTick(() => {
            mobileOrEmail.value = oldValue;
        });
    }
});

function stepsGuard({ to, from }: { to: AuthStep; from?: AuthStep }) {
    // make sure removing the username and clearing errors for next step
    // for token section back and edit works as edit btns which leave us with no choice other than keep the username state
    if (to !== AuthStep.LoginByToken && to !== AuthStep.ChangePasswordToken) {
        if (
            from !== AuthStep.LoginByToken &&
            from !== AuthStep.ChangePasswordToken
        ) {
            mobileOrEmail.value = '';
        }
        nextTick(() => {
            authStore.clearErrors();
        });
    }
}

function back() {
    analyticsStore.trackAll('login_back_click', { step: currentStep.value });
    if (!stepHistory.value.length) {
        return;
    }
    if (stepHistory.value.length === 1) {
        emit('close', false);
    }
    if (currentStep.value === AuthStep.SignupInfo) {
        return backUntil(AuthStep.EnterMobileOrEmail);
    }
    const from = stepHistory.value.pop();
    nextTick(() => stepsGuard({ to: currentStep.value, from }));
}

const submitLogin = /* async */ () => {
    if (tokenDisabled.value) {
        setStep(AuthStep.LoginByPassword);
        return;
    }
    const username = validateUsername(mobileOrEmail.value, 'mobile');
    if (!username) {
        return;
    }
    // should not reach here with email
    try {
        // await authStore.sendToken(username);
        gtm?.trackEvent({
            event: 'mobile field next button',
        });
        setStep(AuthStep.LoginByToken);
    } catch (e) {
        reportException(e);
    }
};
const onSubmit = (text: string, step: AuthStep) => {
    if (step === AuthStep.SwitchAccount && disableSwitchAccount) {
        setStep(AuthStep.Success);
    } else {
        setStep(step);
    }
    if (
        step === AuthStep.ChangePasswordMobile ||
        step === AuthStep.EnterMobileOrEmail
    ) {
        mobileOrEmail.value = '';
    }
    if (currentStep.value === AuthStep.Success) {
        successful = authStore.loggedIn;
        $success(text);
        emit('close', true);
    }
};
const mobilePartialRegex = /09\d{0,9}/;
const tokenDisabled = computed(() => {
    const persianNumbers = convertPersianNumbers(mobileOrEmail.value).trim();
    if (persianNumbers.length === 0) {
        return true;
    }
    if (persianNumbers.length === 1) {
        return true;
    }
    return (
        (Number(persianNumbers) && !mobilePartialRegex.test(persianNumbers)) ||
        !!authStore.error.MobileOrEmail
    );
});

onMounted(() => {
    analyticsStore.trackAll('login_flow_open', { initialStep, openedBy });
    stepHistory.value = [initialStep];
    if (mobile) {
        mobileOrEmail.value = mobile;
    }
});
</script>

<template>
    <mr-modal
        :has-back
        class="auth-modal-box"
        :hide-close="required"
        mobile-page
        :disable-click-to-close="isMobile"
        width="auto"
        @click:back="back"
        @close="emit('close', false)"
    >
        <template #header>
            <div class="login-header">
                <mr-button
                    v-if="hasBack"
                    class="back-btn"
                    :icon-button="{
                        icon: faArrowRight,
                        color: 'var(--cool-gray-900)',
                    }"
                    variant="transparent"
                    @click="back"
                />
                <mr-button
                    v-if="!required"
                    class="close-btn"
                    :icon-button="{
                        icon: faClose,
                        color: 'var(--cool-gray-900)',
                    }"
                    variant="transparent"
                    @click="emit('close', false)"
                />
            </div>
        </template>
        <div class="steps-wrapper">
            <TheEnterMobileOrEmailStep
                v-if="currentStep === AuthStep.EnterMobileOrEmail"
                v-model:username="mobileOrEmail"
                :message
                :rejection-text
                :token-disabled
                @close="emit('close', false)"
                @submit="submitLogin"
                @switch="setStep(AuthStep.LoginByPassword)"
            />
            <TheLoginByPasswordStep
                v-if="currentStep === AuthStep.LoginByPassword"
                v-model:username="mobileOrEmail"
                :token-disabled
                @submit="step => onSubmit('با موفقیت وارد شدید!', step)"
            />
            <TokenStep
                v-if="currentStep === AuthStep.LoginByToken"
                v-model:token="token"
                :mobile="mobileOrEmail"
                @change-mobile="back"
                @switch="setStep(AuthStep.LoginByPassword)"
                @submit="loginByToken"
                @close="emit('close', false)"
            />
            <TheSignupInfoStep
                v-if="currentStep === AuthStep.SignupInfo"
                :mobile="validateUsername(mobileOrEmail)"
                :token
                @submit="
                    () =>
                        onSubmit(
                            'حساب کاربری با موفقیت ایجاد شد.',
                            AuthStep.Success
                        )
                "
            />
            <TokenStep
                v-if="currentStep === AuthStep.ChangePasswordToken"
                v-model:token="token"
                :mobile="mobileOrEmail"
                no-options
                flow="password_reset"
                @submit="checkChangePasswordToken"
                @change-mobile="backUntil(AuthStep.ChangePasswordMobile)"
                @close="emit('close', false)"
            />
            <TheForgetPassPhoneNumber
                v-if="currentStep === AuthStep.ChangePasswordMobile"
                v-model:username="mobileOrEmail"
                :token-disabled
                @submit="setStep"
            />
            <TheChangePassword
                v-if="currentStep === AuthStep.ChangePassword"
                :mobile="validateUsername(mobileOrEmail)"
                :token
                @submit="
                    step => onSubmit('رمز عبور با موفقیت تغییر کرد.', step)
                "
            />
            <TheSwitchAccount
                v-if="currentStep === AuthStep.SwitchAccount"
                @submit="onSubmit('با موفقیت وارد شدید!', AuthStep.Success)"
            />
        </div>
    </mr-modal>
</template>

<style lang="scss" scoped>
.auth-modal-box {
    position: relative;
    background-color: usePalette($p-cool-gray, $p-100);
    min-height: 100% !important;
    max-height: 100vh;
    @include desktop-only {
        background-color: white;
    }
    .login-header {
        @include mobile-only {
            display: flex;
            align-items: center;
            width: 100%;
            padding: 12px;
            height: 60px;
        }

        .close-btn {
            position: absolute;
            left: 0;
            top: 0;
            @include mobile-only {
                display: none;
            }
        }
    }
    :deep() {
        .main-slot-wrapper,
        .title {
            background-color: usePalette($p-cool-gray, $p-100) !important;
            @include mobile-only {
                padding: 0 !important;
            }
            @include desktop-only {
                background-color: white !important;
            }
        }
        .input {
            background-color: white !important;
        }
        .input-container {
            @include desktop-only {
                border: 1px solid usePalette($p-gray, $p-400) !important;
                border-radius: 8px;
            }
        }
        .main-slot-wrapper {
            display: flex;
            flex-direction: column;
        }
    }
}

.steps-wrapper {
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    width: 406px;
    @include mobile-only {
        width: 100%;
        padding-inline: 24px;
        overflow-y: scroll;
    }
}
</style>
